// src/pages/ResultsPublic/CompetitionPartnersSection.jsx
import React from "react";
import { FiExternalLink } from "react-icons/fi";

const FALLBACK_LOGO = "/img/partenaire-placeholder.png";

const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("http")) return photoUrl;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const clean = String(photoUrl).replaceAll("\\", "/").replace(/^\/+/, "");

  return `${base}/${clean}`;
};

export default function CompetitionPartnersSection({
  partenaires = [],
  loading = false,
}) {
  if (loading) {
    return (
      <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-4 h-4 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!partenaires?.length) {
    return (
      <section className="rounded-[24px] border border-dashed border-slate-300 bg-white p-5 text-center dark:border-slate-700 dark:bg-slate-900">
        <h4 className="font-bold text-slate-900 dark:text-white">
          Aucun partenaire affiché
        </h4>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Les partenaires de l’organisateur apparaîtront ici lorsqu’ils sont actifs.
        </p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-slate-800">
        <h4 className="font-bold text-slate-900 dark:text-white">
          Partenaires
        </h4>

        <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
          Cliquez sur un partenaire pour visiter son site.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
        {partenaires.map((partner) => {
          const logo = toAbsolutePhotoUrl(partner.photo_url) || FALLBACK_LOGO;
          const href = String(partner.link_url || "").trim();
          const clickable = Boolean(href);

          const content = (
            <div
              className={`group flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 transition dark:border-slate-800 dark:bg-slate-950/50 ${
                clickable
                  ? "hover:-translate-y-0.5 hover:bg-white hover:shadow-sm dark:hover:bg-slate-900"
                  : "opacity-80"
              }`}
              title={
                clickable
                  ? "Ouvrir dans un nouvel onglet"
                  : "Aucun lien renseigné"
              }
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-slate-900">
                <img
                  src={logo}
                  alt={partner.nom || "Partenaire"}
                  className="h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = FALLBACK_LOGO;
                  }}
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-slate-900 dark:text-white">
                  {partner.nom || "Partenaire"}
                </div>

                <div className="mt-0.5 truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                  {href || "Lien non renseigné"}
                </div>
              </div>

              {clickable ? (
                <FiExternalLink className="shrink-0 text-slate-400 opacity-70 transition group-hover:text-indigo-500 group-hover:opacity-100" />
              ) : null}
            </div>
          );

          if (!clickable) {
            return <div key={partner.id}>{content}</div>;
          }

          return (
            <a
              key={partner.id}
              href={href}
              target="_blank"
              rel="noreferrer"
            >
              {content}
            </a>
          );
        })}
      </div>
    </section>
  );
}